import { useQuery } from 'react-query';
import { baseApiUrl, fetcher, ServerErrorResponse } from 'data_sources/constants';
import { OrganizationTrainingConfig, OrganizationTrainingConfigResponse } from '../../../_types/training';

export async function getOrganizationTrainingConfig(
  orgId: string,
  signal?: AbortSignal
): Promise<OrganizationTrainingConfig> {
  if (!orgId) throw new Error('No organization ID provided.');

  const url = `${baseApiUrl()}/admin-solution/v1/organizations/${orgId}/solutions/training/config`;

  try {
    const response: OrganizationTrainingConfigResponse = await fetcher({
      method: 'GET',
      url,
      options: { signal },
    });

    // Map API response (subscriptions) to internal structure (modules)
    // All returned subscriptions are enabled - the component will merge with solution config
    // Use plan.id and plan.name from the nested plan object
    return {
      modules: (response.subscriptions || []).map((sub) => ({
        id: sub.plan.id,
        name: sub.plan.name,
        status: 'ENABLED' as const,
      })),
    };
  } catch (error: unknown) {
    if (typeof error === 'object' && error !== null && 'errorDetails' in error) {
      throw error as ServerErrorResponse;
    }
    throw error;
  }
}

export default function useOrganizationTrainingConfig(
  orgId: string,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  return useQuery<OrganizationTrainingConfig, ServerErrorResponse>(
    ['organizationTrainingConfig', orgId],
    async ({ signal }): Promise<OrganizationTrainingConfig> => {
      try {
        return await getOrganizationTrainingConfig(orgId, signal);
      } catch (error: unknown) {
        if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
          onError(error as ServerErrorResponse);
        }
        throw error;
      }
    },
    {
      enabled: !!orgId,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
      ...options,
      keepPreviousData: true,
    }
  );
}
